import {
  Skeleton, SkeletonButton, SkeletonChart, SkeletonFilters, SkeletonHeader,
  SkeletonList, SkeletonRegion, SkeletonStats, SkeletonTable, SkeletonText,
} from '@parsina/framework';

import { C } from '../../constants/theme';
import { PANEL_PAD, STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';

/**
 * The reports module's own shape, in placeholder blocks.
 *
 * Page-specific, not generic (DESIGN.md §"Loading & skeleton"): the module's real
 * frame — the white header bar, the rail on its own surface behind a divider, and
 * the work surface with a toolbar card, four stat tiles, a chart and a table, at
 * the sizes they will have. The reader keeps the page's identity through the wait,
 * and the data fills the shape in place rather than shoving the row they were about
 * to click.
 *
 * One `SkeletonRegion` around the lot, so assistive tech hears «در حال بارگذاری»
 * once rather than fourteen times.
 */
export function ReportsSkeletonWeb() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={t('reports.loading')} data-testid="reports-skeleton">
      <div style={{
        display: 'flex', flexDirection: 'column', height: '100%',
        background: C.bg, overflow: 'hidden',
      }}
      >
        {/* ① the page bar — same white, same border, same 64px floor as the real
            one, because the chrome is exactly what a skeleton must not move. */}
        <div style={{
          background: C.card, borderBottom: `1px solid ${C.border}`,
          padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 20,
          minHeight: 64, flexShrink: 0,
        }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width={90} height={10} />
            <Skeleton width={190} height={16} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <SkeletonButton width={120} />
            <SkeletonButton size="icon" />
            <SkeletonButton width={96} />
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{
            width: 176, flexShrink: 0, background: C.card,
            borderInlineEnd: `1px solid ${C.border}`, padding: '12px 8px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonText key={i} width={i % 3 === 0 ? '55%' : '85%'} />
            ))}
          </div>

          <div style={{
            flex: 1, minWidth: 0, padding: PANEL_PAD.regular,
            display: 'flex', flexDirection: 'column', gap: STACK.regular,
          }}
          >
            <SkeletonFilters />
            <SkeletonStats count={4} />
            <SkeletonChart height={260} />
            <SkeletonTable rows={8} columns={6} />
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/**
 * The phone's shape — its OWN skeleton, not the desktop one squeezed.
 *
 * Two tiles across rather than four in a row, a shorter chart, and a list instead of
 * a table, because that is what the mobile surface actually draws. A skeleton that
 * promises a table and delivers cards is a layout jump with extra steps.
 */
export function ReportsSkeletonMobile() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion label={t('reports.loading')} data-testid="reports-skeleton-mobile">
      <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular, padding: 12 }}>
        <SkeletonHeader />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {Array.from({ length: 4 }).map((_, i) => <SkeletonStats key={i} count={1} />)}
        </div>
        <SkeletonChart height={180} />
        <SkeletonList rows={6} />
      </div>
    </SkeletonRegion>
  );
}

export default ReportsSkeletonWeb;
